import { Component, computed, signal, model, input, output, ElementRef, Renderer2, OnDestroy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { focusInitial, isTopDialog, nextDialogId, popDialog, pushDialog, trapTab } from '../../utils/dialog-a11y.util';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './app-confirm-dialog.component.html',
  styleUrl: './app-confirm-dialog.component.css',
  host: { '(document:keydown)': 'onKeydown($event)' },
})
export class AppConfirmDialogComponent implements OnChanges, OnDestroy {
  // Two-way signal for modal visibility
  visible = model(false);

  // Configuration Inputs
  title = input<string>('Confirmación');
  description = input<string>('');
  icon = input<string>('pi pi-trash');
  confirmLabel = input<string>('Confirmar');
  cancelLabel = input<string>('Cancelar');
  confirmType = input<'primary' | 'danger' | 'success' | 'warning' | 'neutral'>('primary');
  loading = input<boolean>(false);

  /** Motivo obligatorio (borrados lógicos, anulaciones): muestra un cuadro de texto y bloquea Confirmar hasta que sea válido. */
  requireReason = input<boolean>(false);
  reasonLabel = input<string>('Motivo');
  reasonPlaceholder = input<string>('Escribe el motivo…');
  reasonMin = input<number>(5);
  reasonMax = input<number>(100);
  reason = model<string>('');
  protected readonly reasonId = nextDialogId('cfm-reason');
  protected readonly reasonLength = computed(() => this.reason().trim().length);
  protected readonly reasonTouched = signal(false);
  /** Error visible solo tras escribir/salir del campo, nunca al abrir el diálogo. */
  protected readonly reasonError = computed(() => {
    if (!this.requireReason() || !this.reasonTouched() || this.reasonValid()) return '';
    return this.reasonLength() > this.reasonMax()
      ? `El motivo no puede superar ${this.reasonMax()} caracteres`
      : `Escribe al menos ${this.reasonMin()} caracteres`;
  });
  protected readonly reasonValid = computed(() => !this.requireReason() || (this.reasonLength() >= this.reasonMin() && this.reasonLength() <= this.reasonMax()));

  // Outputs
  onConfirm = output<void>();
  onCancel = output<void>();

  @ViewChild('confirmPortal', { static: true }) confirmPortal!: ElementRef<HTMLElement>;

  // ── Accesibilidad (ver shared/utils/dialog-a11y.util.ts) ───────────────────
  @ViewChild('panel') panel?: ElementRef<HTMLElement>;
  protected readonly titleId = nextDialogId('cfm-title');
  protected readonly descId = nextDialogId('cfm-desc');
  private readonly stackId = Symbol('dialog');
  private opener: HTMLElement | null = null;
  private active = false;

  onKeydown(event: KeyboardEvent): void {
    if (!this.visible() || !isTopDialog(this.stackId)) return;
    if (event.key === 'Escape') { event.preventDefault(); this.cancel(); }
    else if (event.key === 'Tab' && this.panel) trapTab(event, this.panel.nativeElement);
  }

  private activate(): void {
    if (this.active) return;
    this.active = true;
    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    pushDialog(this.stackId);
    setTimeout(() => { if (this.panel) focusInitial(this.panel.nativeElement); }, 0);
  }

  private deactivate(restoreFocus = true): void {
    if (!this.active) return;
    this.active = false;
    popDialog(this.stackId);
    const opener = this.opener;
    this.opener = null;
    if (restoreFocus && opener && document.contains(opener)) setTimeout(() => opener.focus(), 0);
  }

  private isAppendedToBody = false;
  private originalParent: HTMLElement | null = null;

  constructor(private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible()) { this.reason.set(''); this.reasonTouched.set(false); this.appendToBody(); this.activate(); } else { this.removeFromBody(); this.deactivate(); }
    }
  }

  ngOnDestroy(): void {
    this.removeFromBody();
    this.deactivate(false);
  }

  confirm() {
    if (this.loading() || !this.reasonValid()) return;
    this.onConfirm.emit();
    this.visible.set(false);
  }

  cancel() {
    if (this.loading()) return;
    this.onCancel.emit();
    this.visible.set(false);
  }

  private appendToBody(): void {
    if (!this.isAppendedToBody && this.confirmPortal) {
      const el = this.confirmPortal.nativeElement;
      this.originalParent = el.parentElement;
      this.renderer.appendChild(document.body, el);
      this.isAppendedToBody = true;
      this.renderer.addClass(document.body, 'overflow-hidden');
    }
  }

  private removeFromBody(): void {
    if (this.isAppendedToBody && this.confirmPortal && this.originalParent) {
      this.renderer.appendChild(this.originalParent, this.confirmPortal.nativeElement);
      this.isAppendedToBody = false;
      this.renderer.removeClass(document.body, 'overflow-hidden');
    }
  }

  getIconColorClass(): string {
    const type = this.confirmType();
    switch (type) {
      case 'danger':
        return 'bg-gradient-to-b from-danger-500 to-danger-600 text-white border-4 border-white';
      case 'warning':
        return 'bg-warning-500 text-white border-4 border-white';
      case 'success':
        return 'bg-gradient-to-b from-success-400 to-success-500 text-white border-4 border-white';
      case 'primary':
        return 'bg-gradient-to-b from-primary-500 to-primary-600 text-white border-4 border-white';
      default:
        return 'bg-gradient-to-b from-slate-500 to-slate-600 text-white border-4 border-white';
    }
  }

  getConfirmButtonClass(): string {
    const type = this.confirmType();
    switch (type) {
      case 'danger':
        return 'bg-danger-600 hover:bg-danger-500 text-white';
      case 'warning':
        return 'bg-warning-500 hover:bg-warning-600 text-white font-bold shadow-md shadow-warning-500/20';
      case 'success':
        return 'bg-success-600 hover:bg-success-500 text-white';
      case 'primary':
        return 'bg-primary-500 hover:bg-primary-600 text-white';
      default:
        return 'bg-slate-600 hover:bg-slate-500 text-white';
    }
  }
}
